import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  getOperatingSystem,
  isValidEmail,
  trackWithCallback,
} from "@tomocredit/common-fe/utils";
import AppStore from "@tomocredit/common-fe/assets/svg/app-store-badge_apple.svg";
import PlayStore from "@tomocredit/common-fe/assets/svg/app-store-badge_google.svg";
import TomoAppIcon from "@tomocredit/common-fe/assets/svg/app-icon.svg";
import {
  BOOST_APP_URL_IOS,
  BOOST_APP_URL_ANDROID,
} from "@tomocredit/common-fe/consts";
import HandHoldingCreditTab from "images/boost/hand-holding-credit-tab.webp";
import CheckMark from "@tomocredit/common-fe/components/CheckMark";
import BoostHeader from "components/BoostHeader";
import { BreakpointContext } from "components/common";

/* UTILS */
const customRedirect = (url: string) => {
  window.location.href = url;
};

/* MAIN COMPONENT */
const AppRedirectV2 = () => {
  const location = useLocation();
  const { isBelow } = useContext(BreakpointContext);
  const { search } = location;
  const params = new URLSearchParams(search);
  const email = params.get("email") || "";
  const userAgent = getOperatingSystem();
  const belowMd = isBelow("md");
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(true);

  const buildAnalyticPayload = () => {
    let emailFinal = "";
    if (isValidEmail(email)) {
      emailFinal = email;
    }
    const analyticsPayload = {
      userAgent,
      ...(emailFinal && { email: emailFinal }),
    };
    return analyticsPayload;
  };

  // Handle button click to change QR code
  const handleClick = (type: "iOS" | "Android") => {
    const storeUrl = type === "iOS" ? BOOST_APP_URL_IOS : BOOST_APP_URL_ANDROID;
    trackWithCallback(`APP_STORE_CLICK_${type.toUpperCase()}`, { userAgent }, () => customRedirect(storeUrl));
  };

  useEffect(() => {
    // window.location.href will immediately load new page
    // this is to ensure image are properly loaded before the redirect
    if (!imagesLoaded || !shouldRedirect) {
      return;
    }
    const analyticsPayload = buildAnalyticPayload();
    // Set initial QR code based on user's device
    if (userAgent === "iOS") {
      trackWithCallback("APP_STORE_REDIRECT_IOS", analyticsPayload, () => customRedirect(BOOST_APP_URL_IOS));
    } else if (userAgent === "Android") {
      trackWithCallback("APP_STORE_REDIRECT_ANDROID", analyticsPayload, () => customRedirect(BOOST_APP_URL_ANDROID));
    } else {
      trackWithCallback("APP_STORE_REDIRECT", analyticsPayload, undefined);
      setShouldRedirect(false);
    }
  }, [imagesLoaded, shouldRedirect]);

  // add a short delay to ensure img loading
  useEffect(() => {
    // keep track of images that need to load
    let loadedCount = 0;
    const checkAllLoaded = () => {
      loadedCount++;
      if (loadedCount >= 3) {
        setImagesLoaded(true);
      }
    };
    const appStoreImage = new Image();
    appStoreImage.onload = checkAllLoaded;
    appStoreImage.onerror = checkAllLoaded;
    appStoreImage.src = AppStore;

    const playStoreImage = new Image();
    playStoreImage.onload = checkAllLoaded;
    playStoreImage.onerror = checkAllLoaded;
    playStoreImage.src = PlayStore;

    const qrIconImage = new Image();
    qrIconImage.onload = checkAllLoaded;
    qrIconImage.onerror = checkAllLoaded;
    qrIconImage.src = TomoAppIcon;

    // failsafe timeout
    const uiTimeout = setTimeout(() => {
      setImagesLoaded(true);
    }, 500);
    return () => clearTimeout(uiTimeout);
  }, []);

  return (
    <div
      className="w-full h-full relative flex flex-col"
      style={{
        minHeight: "100vh",
        width: "100vw",
        backgroundColor: "#201E25",
        minWidth: 375,
        backgroundImage: "radial-gradient(circle at 15% 20%, rgba(253, 226, 186, 0.4), transparent 10%), radial-gradient(circle at 32% 30%, rgba(181, 248, 208, 0.3), transparent 16%), radial-gradient(circle at 51% 20%, rgba(181, 240, 248, 0.3), transparent 16%), radial-gradient(circle at 85% 25%, rgba(181, 184, 248, 0.3), transparent 16%)",
      }}
    >
      <div className="w-full">
        <BoostHeader />
      </div>
      <div className="flex-1 flex items-center">
        <div className="h-full py-16 flex flex-col items-start mx-8" style={{ maxWidth: belowMd ? undefined : 480 }}>
          <div className="w-full" style={{ fontSize: belowMd ? 20 : 28 }}>
            Meet the AI Credit Engine that{" "}
            <span className="font-bold">
              Manages & Builds
            </span>{" "}
            Your Credit
          </div>
          <div className="mt-8 text-lg flex flex-col items-start text-left whitespace-nowrap space-y-4">
            <span className="font-medium">Build Credit Fast.</span>
            <CheckMark className="" color="boost">
              <span className="ml-3">
                Up to <span className="font-bold"> $30,000 credit line</span>
              </span>
            </CheckMark>
            <CheckMark className="" color="boost">
              <span className="ml-3">
                <span className="font-bold">105+ </span>
                <span>credit boost</span>
              </span>
            </CheckMark>
            <CheckMark className="" color="boost">
              <span className="ml-3 relative">
                No credit checks. <span className="font-bold">Ever.</span>
              </span>
            </CheckMark>
          </div>
          {/* Store selection buttons */}
          <div className="mt-20 flex space-x-4 md:space-x-8 justify-between w-full">
            <div className="flex flex-col space-y-4 md:space-y-8">
              <button
                type="button"
                onClick={() => handleClick("iOS")}
                className="focus:outline-none hover:opacity-80 transition-opacity duration-200 w-full"
                style={{
                  width: belowMd ? 180 : 240,
                  height: belowMd ? 52 : 69,
                  backgroundImage: `url(${AppStore})`,
                  backgroundSize: "100% 100%",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
              />
              <button
                type="button"
                onClick={() => handleClick("Android")}
                className="focus:outline-none hover:opacity-80 transition-opacity duration-200"
                style={{
                  width: belowMd ? 180 : 240,
                  height: belowMd ? 52 : 69,
                  backgroundImage: `url(${PlayStore})`,
                  backgroundSize: "100% 100%",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
              />
            </div>
            {/* Display QR code */}
            <div className="flex flex-col items-center">
              <QRCodeSVG
                id="app-qr-code"
                value={`${process.env.REACT_APP_DOMAIN}/app`}
                size={belowMd ? 120 : 200}
                bgColor="white"
                fgColor="black"
                level="H"
                imageSettings={{
                  src: TomoAppIcon,
                  width: belowMd ? 40 : 50,
                  height: belowMd ? 40 : 50,
                  excavate: true,
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <div
        className="absolute right-0 bottom-0 hidden md:block"
        style={{
          maxWidth: "50%",
          maxHeight: "calc(100% - 80px)",
          overflow: "hidden",
        }}
      >
        <img alt="" src={HandHoldingCreditTab} />
      </div>
    </div>
  );
};

export default AppRedirectV2;